import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccessTokenService } from 'src/app/Services/access-token.service';
import { DynamicDataService } from 'src/app/Services/dynamic-data.service';
import { PowerbiService } from 'src/app/Services/powerbi.service';
import { MaterialModule } from 'src/app/Shared/MaterialModule/material/material.module';

interface NavSearchResult {
  title: string;
  link: string;
  icon: string;
  parentTitle?: string;
  resultType?: 'pbi';
}

const PBI_CACHES = [
  { baseKey: 'spend',    responseKey: 'spend',    route: '/dashboard',          label: 'Spend Dashboard' },
  { baseKey: 'budget',   responseKey: 'budget',   route: '/Budget-Dashboard',   label: 'Budget Dashboard' },
  { baseKey: 'mis',      responseKey: 'mis',      route: '/MIS-Dashboard',      label: 'MIS Dashboard' },
  { baseKey: 'contract', responseKey: 'contract', route: '/Contract-Dashboard', label: 'Contract Dashboard' },
];

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule, TranslateModule],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: { style: 'flex: 1; min-width: 0; display: flex;' },
})
export class GlobalSearchComponent implements OnInit {
  searchQuery = '';
  searchOpen = false;
  searchResults: NavSearchResult[] = [];
  private warmingInProgress = false;
  private pbiPageCache: Record<string, { name: string; displayName: string }[]> | null = null;

  constructor(
    private dynamicDataService: DynamicDataService,
    private router: Router,
    private powerbiService: PowerbiService,
    private accessTokenService: AccessTokenService,
  ) {}

  ngOnInit(): void {
    this.warmPbiCachesIfNeeded();
  }

  private warmPbiCachesIfNeeded(): void {
    if (this.warmingInProgress) return;
    const orgId = this.accessTokenService.getOrganizationId();
    if (!orgId) return;
    if (PBI_CACHES.every(c => !!localStorage.getItem(this.powerbiService.pbiCacheKey(c.baseKey, orgId)!))) return;

    this.warmingInProgress = true;
    this.powerbiService.getReportPages({ Organization_Id: orgId }).subscribe({
      next: (res: any) => {
        PBI_CACHES.forEach(cache => {
          const pages = this.transformPbiPages(res?.[cache.responseKey] ?? [], cache.baseKey);
          if (pages.length > 0) {
            localStorage.setItem(this.powerbiService.pbiCacheKey(cache.baseKey, orgId)!, JSON.stringify(pages));
          }
        });
        this.pbiPageCache = null;
        this.warmingInProgress = false;
        if (this.searchOpen && this.searchQuery.trim().length >= 2) {
          this.onSearchInput(this.searchQuery);
        }
      },
      error: () => { this.warmingInProgress = false; },
    });
  }

  private transformPbiPages(rawPages: any[], baseKey: string): { name: string; displayName: string }[] {
    const isMember = this.accessTokenService.isUserMember?.() ?? false;
    const applyMemberFilter = isMember && (baseKey === 'spend' || baseKey === 'budget');
    const result: { name: string; displayName: string }[] = [];
    rawPages.forEach((page: any) => {
      const dn: string = page.displayName ?? '';
      if (dn.trim().toLowerCase().startsWith('hide')) return;
      const firstWord = dn.trim().split(' ')[0];
      if (applyMemberFilter && firstWord === 'Supplier') return;
      if (firstWord === 'Pre') {
        result.push({ name: page.name, displayName: dn.replace('Pre ', '') });
      } else if (firstWord === 'Post') {
        result.push({ name: page.name, displayName: dn.replace('Post ', '') });
      } else {
        result.push({ name: page.name, displayName: dn });
      }
    });
    return result;
  }

  isIconFont(icon: string): boolean {
    return typeof icon === 'string' && (
      icon.startsWith('icon-') || icon.startsWith('spendkey-') || icon.startsWith('sk-side-')
    );
  }

  onSearchInput(query: string): void {
    const trimmed = query.trim().toLowerCase();
    if (trimmed.length < 2) {
      this.searchResults = [];
      this.searchOpen = false;
      return;
    }

    const raw: any[] = this.dynamicDataService.getDataFromLocal('SideBar') ?? [];
    const parentTitles = new Map<number, string>();
    raw.forEach((item: any) => {
      if (!item.ParentId) parentTitles.set(+item.Id, item.Title);
    });
    const navResults: NavSearchResult[] = raw
      .filter((item: any) => item.Link && (item.Title ?? '').toLowerCase().includes(trimmed))
      .slice(0, 6)
      .map((item: any) => ({
        title: item.Title,
        link: item.Link,
        icon: item.Icon ?? 'chevron_right',
        parentTitle: item.ParentId ? parentTitles.get(+item.ParentId) : undefined,
      }));

    const pbiResults: NavSearchResult[] = [];
    const pagesByBaseKey = this.getPbiPages();
    if (pagesByBaseKey) {
      for (const cache of PBI_CACHES) {
        pagesByBaseKey[cache.baseKey]
          .filter(p => p.displayName.toLowerCase().includes(trimmed))
          .forEach(p => {
            pbiResults.push({
              title: p.displayName,
              link: `${cache.route}?page=${encodeURIComponent(p.name)}`,
              icon: 'bar_chart',
              parentTitle: cache.label,
              resultType: 'pbi',
            });
          });
      }
    }

    this.searchResults = [...navResults, ...pbiResults].slice(0, 10);
    this.searchOpen = true;
  }

  private getPbiPages(): Record<string, { name: string; displayName: string }[]> | null {
    if (this.pbiPageCache) return this.pbiPageCache;
    const orgId = this.accessTokenService.getOrganizationId();
    if (!orgId) return null;
    const parsed: Record<string, { name: string; displayName: string }[]> = {};
    for (const cache of PBI_CACHES) {
      const storageKey = this.powerbiService.pbiCacheKey(cache.baseKey, orgId)!;
      try {
        parsed[cache.baseKey] = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
      } catch {
        parsed[cache.baseKey] = [];
        localStorage.removeItem(storageKey);
      }
    }
    this.pbiPageCache = parsed;
    return parsed;
  }

  navigateToResult(link: string): void {
    this.router.navigateByUrl(link);
    this.clearSearch();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.searchOpen = false;
  }

  closeSearch(): void {
    this.searchOpen = false;
  }
}
